import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import { EscCloseButton } from 'tailwind-v4/components/esc-close-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'tailwind-v4/components/sheet'
import { useAccess } from '@/access'
import { showsComingSoon } from '@/components/nav-v4/config'
import { useIsMobile } from '@/hooks/use-mobile'
import { useWhatsNewSeen } from '@/hooks/use-whats-new-seen'
import { useWhatsNewTracking } from '@/hooks/use-whats-new-tracking'
import {
  FEATURES,
  SALES_HREF,
  featureHref,
  type WhatsNewFeature,
} from './features'

/**
 * The once-only "what's new" pop-up.
 *
 * Mounted once at the org layout, beside the other global dialogs. Fires on the
 * first login after a release, for the people who could act on it, and never
 * again once dismissed.
 *
 * ── WHO SEES IT ─────────────────────────────────────────────────────────────
 *
 * `showsComingSoon` — non-staff holding `bookings.settings` — imported from the
 * nav config rather than re-derived. That predicate already decides who sees
 * these three in the sidebar, and the whole point of it is that FOH and
 * reservations roles keep operational-only menus. A pop-up is a far ruder
 * interruption than a menu row, so if anything it wants the gate MORE than the
 * nav does. A second copy of the rule would drift and the failure mode is
 * blocking a floor phone mid-service.
 *
 * ── WHEN IT FIRES ───────────────────────────────────────────────────────────
 *
 * Only on the org home. Someone who deep-linked into a booking, a live day
 * planner or a report is mid-task, and a marketing dialog over the top of that
 * is exactly the behaviour people disable pop-ups for. Home is the one place
 * where nothing is in progress.
 *
 * It also waits for access to resolve. Rendering off an unresolved subject
 * would flash the dialog at everyone for a frame, including the staff and
 * floor users it is meant to skip.
 *
 * ── PREVIEWING IT ───────────────────────────────────────────────────────────
 *
 * `?whats-new=preview` on the org home forces it open, ignoring both the
 * audience gate and the seen flag. Without this the dialog is effectively
 * untestable by the people who need to sign it off: every Stampede address is
 * staff, and staff are precisely who the gate excludes, so an internal reviewer
 * would log in, see nothing, and have no way to tell "correctly hidden" from
 * "broken". It only ever forces the dialog OPEN, so it cannot hide anything
 * from a real customer, and it does not mark the announcement as seen.
 */

function FeatureRow({
  feature,
  onNavigate,
  orgId,
}: {
  feature: WhatsNewFeature
  onNavigate: () => void
  orgId: string
}) {
  return (
    <li className="flex items-center gap-4 py-4">
      <feature.art />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-semibold">{feature.name}</span>
        <span className="text-[13px] leading-snug text-muted-foreground">
          {feature.line}
        </span>
      </div>
      <Button
        className="ml-auto shrink-0"
        onClick={onNavigate}
        render={<Link to={featureHref(orgId, feature.id)} />}
        rightIcon={<ArrowRight />}
        size="sm"
        variant="ghost"
      >
        View
      </Button>
    </li>
  )
}

export function WhatsNewDialog() {
  const { org_id: orgId } = useParams<{ org_id: string }>()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const { subject } = useAccess()
  const { markSeen, seen } = useWhatsNewSeen()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  // One narrowed id: `useParams` hands back `string | undefined`, and a bare
  // `Boolean(orgId) &&` guard does not narrow it for the template literal below.
  const org = orgId ?? ''
  const preview = searchParams.get('whats-new') === 'preview'
  const onOrgHome = org !== '' && pathname.replace(/\/$/, '') === `/${org}`
  const eligible =
    org !== '' && showsComingSoon({ access: subject, orgId: org })

  const whatsNewTracking = useWhatsNewTracking(org, 'popup')

  // One popup_viewed per announcement per session, not one per effect run.
  // `seen` stays false while the dialog is open, so the open-effect's deps
  // re-fire on any access recompute or StrictMode remount — without the ref
  // guard that would double-count views. `seen` flipping true (dismissal)
  // resets nothing; a fresh announcement id remounts with a clean ref.
  const viewedRef = useRef(false)

  useEffect(() => {
    const shouldOpen = preview || (!seen && onOrgHome && eligible)
    if (!shouldOpen) return
    setOpen(true)
    if (!viewedRef.current) {
      viewedRef.current = true
      whatsNewTracking.popupViewed({ preview })
    }
    // The effect decides WHETHER to open; tracking fires once, guarded by the
    // ref above, so deps stay the decision inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, seen, onOrgHome, eligible])

  /**
   * The ONLY exit path. Dismissing and following a link both count as having
   * seen it — except in preview, which must not burn the announcement for
   * whoever is testing.
   *
   * `reason` keeps dismissal analytics honest: only an actual close (esc,
   * backdrop, close button) records as dismissed. Leaving via "See all", a
   * feature row or the sales CTA is engagement, and each of those tracks its
   * own event at its own call site before calling this.
   */
  const close = (
    reason: 'dismiss' | 'see_all' | 'feature' | 'sales' = 'dismiss'
  ) => {
    setOpen(false)
    if (!preview) markSeen()
    if (reason === 'dismiss') {
      whatsNewTracking.popupDismissed({ preview })
    }
  }

  if (!orgId) return null

  const title = 'Three new features.'
  const description =
    'Built and released. If one of these is not on your account yet, we can turn it on.'

  const rows = (
    <ul className="flex flex-col divide-y divide-border">
      {FEATURES.map((feature) => (
        <FeatureRow
          feature={feature}
          key={feature.id}
          onNavigate={() => {
            whatsNewTracking.featureClicked(feature, { preview })
            close('feature')
          }}
          orgId={orgId}
        />
      ))}
    </ul>
  )

  const actions = (
    <>
      <Button
        onClick={() => {
          whatsNewTracking.seeAllClicked({ preview })
          close('see_all')
        }}
        render={<Link to={`/${orgId}/whats-new`} />}
        variant="ghost"
      >
        See all
      </Button>
      <Button
        onClick={() => {
          whatsNewTracking.salesClicked({ preview })
          close('sales')
          window.open(SALES_HREF, '_blank', 'noreferrer')
        }}
        variant="primary"
      >
        Speak to the team
      </Button>
    </>
  )

  // Bottom sheet on a phone, dialog on desktop — the house rule for anything
  // this tall. Capped so the actions stay reachable without the sheet
  // swallowing the viewport.
  if (isMobile) {
    return (
      <Sheet
        onOpenChange={(next) => {
          if (next) setOpen(true)
          else close()
        }}
        open={open}
      >
        <SheetContent className="max-h-[90dvh]" side="bottom">
          <SheetHeader className="pr-12">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4 overflow-y-auto px-4 py-4 scroll-py-4">
            {rows}
            <div className="flex flex-col gap-2">{actions}</div>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog
      onOpenChange={(next) => {
        if (next) setOpen(true)
        else close()
      }}
      open={open}
    >
      <DialogContent className="sm:max-w-lg" showCloseButton={false}>
        {/* names its own shortcut, and replaces the default X rather than
            sitting beside it */}
        <EscCloseButton />
        {/* clears the absolutely-positioned esc chip (right-4 + ~36px wide),
            so the title and description never run underneath it */}
        <DialogHeader className="pr-14">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {rows}
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
